import React from 'react'
import { Link } from 'react-router-dom'
import { Calculator, Phone, Mail, MapPin, Star, StarHalf } from 'lucide-react'

export default function Footer() {
  return (
    <footer className="bg-white border-t border-stone-200 text-stone-700">
      <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8 lg:py-16">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-10">
          {/* Brand Column */}
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <img 
                src="https://assets.auxodev.dk/assets/57eb0739-2c0e-4813-8144-be81ba542e2d/thellefsen-regnskab-logo-hvid.png" 
                alt="Thellefsen Regnskab - Revisor for mindre selskaber og selvstændige" 
                className="h-14 w-auto object-contain"
              />
            </div>
            <p className="text-sm text-stone-600 leading-relaxed">
              Personlig revisorhjælp til fast pris. Vi sikrer dig fuldstændig ro i maven omkring din virksomheds økonomi og skat.
            </p>
            <div className="flex items-center gap-1 text-[#18724a]">
              {[...Array(4)].map((_, i) => (
                <Star key={i} className="h-4 w-4 fill-current" />
              ))}
              <svg className="h-4 w-4 text-[#18724a]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <defs>
                  <linearGradient id="grad-footer-star" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="76%" stopColor="currentColor" />
                    <stop offset="76%" stopColor="transparent" />
                  </linearGradient>
                </defs>
                <polygon 
                  points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" 
                  fill="url(#grad-footer-star)"
                />
              </svg>
              <span className="text-xs font-semibold text-stone-800 ml-1">
                4,76 / 5 på Ageras
              </span>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-sm font-semibold text-stone-900 uppercase tracking-wider mb-4">
              Ydelser
            </h3>
            <ul className="space-y-2.5 text-sm">
              <li>
                <Link to="/regnskabsservice" className="hover:text-orange-600 transition-colors font-medium">Regnskabsservice</Link>
              </li>
              <li>
                <Link to="/bogfoering" className="hover:text-orange-600 transition-colors">Bogføring</Link>
              </li>
              <li>
                <Link to="/aarsrapport-og-aarsregnskab" className="hover:text-orange-600 transition-colors">Årsrapport & Årsregnskab</Link>
              </li>
              <li>
                <Link to="/ydelser/personlig-selvangivelse" className="hover:text-orange-600 transition-colors">Personlig selvangivelse</Link>
              </li>
              <li className="pt-1 border-t border-stone-200">
                <Link to="/priser" className="hover:text-orange-600 transition-colors font-semibold text-stone-900">Se priser & pakker &rarr;</Link>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-sm font-semibold text-stone-900 uppercase tracking-wider mb-4">
              Direkte kontakt
            </h3>
            <ul className="space-y-3 text-sm">
              <li className="flex items-center gap-2.5">
                <Phone className="h-4 w-4 text-stone-900 shrink-0" />
                <a href="tel:+4531335470" className="hover:text-orange-600 transition-colors">
                  31 33 54 70
                </a>
              </li>
              <li className="flex items-center gap-2.5">
                <Mail className="h-4 w-4 text-stone-900 shrink-0" />
                <a href="mailto:info@tregnskab.dk" className="hover:text-orange-600 transition-colors">
                  info@tregnskab.dk
                </a>
              </li>
              <li className="flex items-start gap-2.5">
                <MapPin className="h-4 w-4 text-stone-900 shrink-0 mt-0.5" />
                <span className="text-stone-600">
                  3060 Espergærde
                </span>
              </li>
            </ul>
          </div>

          {/* Opening Hours */}
          <div>
            <h3 className="text-sm font-semibold text-stone-900 uppercase tracking-wider mb-4">
              Åbningstider
            </h3>
            <ul className="space-y-2 text-sm text-stone-600">
              <li className="flex justify-between">
                <span>Mandag - Torsdag:</span>
                <span className="font-medium text-stone-900">08:30 - 16:30</span>
              </li>
              <li className="flex justify-between">
                <span>Fredag:</span>
                <span className="font-medium text-stone-900">08:30 - 15:30</span>
              </li>
              <li className="flex justify-between">
                <span>Lørdag - Søndag:</span>
                <span className="text-stone-500">Lukket</span>
              </li>
              <li className="pt-2 border-t border-stone-200 mt-2 text-xs text-orange-600 font-medium">
                Hurtig respons inden for 24 timer
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-12 pt-8 border-t border-stone-200 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-stone-500">
          <p>© {new Date().getFullYear()} Thellefsen Regnskab ApS. CVR: 42120103. Alle rettigheder forbeholdes.</p>
          <div className="flex items-center gap-6">
            <a 
              href="https://www.iubenda.com/privacy-policy/56558995" 
              className="iubenda-white iubenda-noiframe iubenda-embed hover:text-stone-800 transition-colors" 
              title="Privatlivspolitik"
            >
              Privatlivspolitik
            </a>
            <a 
              href="https://www.iubenda.com/privacy-policy/56558995/cookie-policy" 
              className="iubenda-white iubenda-noiframe iubenda-embed hover:text-stone-800 transition-colors" 
              title="Cookiepolitik"
            >
              Cookiepolitik
            </a>
            <Link to="/kontakt" className="hover:text-stone-800 transition-colors">Handelsbetingelser</Link>
          </div>
        </div>
      </div>
    </footer>
  )
}
